import { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import {
  CurrentInformationRequest,
  DocumentProgress,
  QuestionProgress,
} from 'dashboard409a/pages/Information/CurrentRequestedDocumentsAndQuestions/types';
import { getCurrentInformationRequests } from 'dashboard409a/services/dashboard';
import { Logger } from 'utilities';

export interface UseInformation409AProps {
  currentCompanyMeasurementDateId?: number | null;
}

const useInformation409A = ({ currentCompanyMeasurementDateId }: UseInformation409AProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [currentInformationRequests, setCurrentInformationRequests] = useState<CurrentInformationRequest[]>([]);

  const handleGetCurrentInformationRequests = useCallback(async () => {
    try {
      if (currentCompanyMeasurementDateId) {
        setIsLoading(true);
        const currentRequests = await getCurrentInformationRequests(currentCompanyMeasurementDateId);
        if (!isEmpty(currentRequests)) {
          setCurrentInformationRequests(currentRequests);
          setHasLoaded(true);
        } else {
          setCurrentInformationRequests([]);
          setHasLoaded(true);
        }
      } else {
        setCurrentInformationRequests([]);
        setHasLoaded(false);
      }
    } catch (error) {
      Logger.error('Error getting current information requests', error);
      enqueueSnackbar('Error getting current information requests', { variant: 'error' });
      setCurrentInformationRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentCompanyMeasurementDateId, enqueueSnackbar]);

  useEffect(() => {
    handleGetCurrentInformationRequests();
  }, [handleGetCurrentInformationRequests]);

  const isDocumentUploaded = (progress: DocumentProgress | QuestionProgress): boolean =>
    'is_uploaded' in progress ? progress.is_uploaded : true;

  const isQuestionAnswered = (progress: DocumentProgress | QuestionProgress): boolean =>
    'answers' in progress ? !isEmpty(progress.answers) : true;

  const isRequestComplete = (request: CurrentInformationRequest): boolean =>
    'progress' in request && request.progress.every(isDocumentUploaded) && request.progress.every(isQuestionAnswered);

  const checkIfAllQuestionsAnsweredAndDocumentsAreUploaded = (
    currentInformationRequestsParams: CurrentInformationRequest[]
  ): boolean => currentInformationRequestsParams.every(isRequestComplete);

  return {
    handleGetCurrentInformationRequests,
    checkIfAllQuestionsAnsweredAndDocumentsAreUploaded,
    isLoading,
    hasLoaded,
    setHasLoaded,
    currentInformationRequests,
  };
};

export default useInformation409A;
